import { type FC, useMemo, useCallback } from "react";

import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useEstateAddresses } from "@/shared/hooks/useEstateAddresses";
import { parseAddress, toStateAbbreviation } from "@/shared/utils/addressUtils";
import type { AddressParts } from "@/shared/utils/addressUtils";

const NEW_ADDRESS_VALUE = "__new_address__";

interface AddressBlockProps {
  prefix: string;
  values: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  dialogRef?: React.RefObject<HTMLDivElement | null>;
  hasError?: (key: string) => boolean;
  estateId?: number;
}

export const AddressBlock: FC<AddressBlockProps> = ({
  prefix,
  values,
  onFieldChange,
  dialogRef,
  hasError,
  estateId,
}) => {
  const { data: existingAddresses } = useEstateAddresses(estateId);
  const options = useMemo(() => existingAddresses ?? [], [existingAddresses]);
  const showSelector = !!estateId && options.length > 0;

  const selectedId = values[`${prefix}_address_id`] ?? "";
  const selectValue = /^\d+$/.test(selectedId) ? selectedId : NEW_ADDRESS_VALUE;

  const address: AddressParts = useMemo(
    () => ({
      streetAddress: values[`${prefix}_address`] ?? "",
      apartment: values[`${prefix}_apartment`] ?? "",
      city: values[`${prefix}_city`] ?? "",
      state: values[`${prefix}_state`] ?? "",
      zipCode: values[`${prefix}_zip`] ?? "",
    }),
    [values, prefix],
  );

  const handleChange = useCallback(
    (updated: AddressParts) => {
      onFieldChange(`${prefix}_address`, updated.streetAddress);
      onFieldChange(`${prefix}_apartment`, updated.apartment);
      onFieldChange(`${prefix}_city`, updated.city);
      onFieldChange(`${prefix}_state`, updated.state);
      onFieldChange(`${prefix}_zip`, updated.zipCode);
      onFieldChange(`${prefix}_address_id`, "");
    },
    [prefix, onFieldChange],
  );

  const handleSelectChange = useCallback(
    (value: string) => {
      if (value === NEW_ADDRESS_VALUE) {
        onFieldChange(`${prefix}_address_id`, "");
      } else {
        const option = options.find((o) => String(o.addressId) === value);
        if (option) {
          const parts = parseAddress(option.label);
          onFieldChange(`${prefix}_address`, parts.streetAddress);
          onFieldChange(`${prefix}_apartment`, parts.apartment);
          onFieldChange(`${prefix}_city`, parts.city);
          onFieldChange(`${prefix}_state`, toStateAbbreviation(parts.state));
          onFieldChange(`${prefix}_zip`, parts.zipCode);
          onFieldChange(`${prefix}_address_id`, value);
        }
      }
    },
    [options, onFieldChange, prefix],
  );

  const addressErrors = useMemo(() => {
    if (!hasError) return undefined;
    const errs: Partial<Record<keyof AddressParts, string>> = {};
    if (hasError(`${prefix}_address`)) errs.streetAddress = "Required";
    if (hasError(`${prefix}_city`)) errs.city = "Required";
    if (hasError(`${prefix}_state`)) errs.state = "Required";
    if (hasError(`${prefix}_zip`)) errs.zipCode = "Required";
    return Object.keys(errs).length > 0 ? errs : undefined;
  }, [hasError, prefix]);

  return (
    <div className="space-y-3">
      {showSelector && (
        <div className="space-y-1.5">
          <label className="text-sm text-secondary">Use an existing address</label>
          <Select value={selectValue} onValueChange={handleSelectChange}>
            <SelectTrigger aria-label="Use an existing address">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.addressId} value={String(option.addressId)}>
                  {option.label}
                </SelectItem>
              ))}
              <SelectItem value={NEW_ADDRESS_VALUE}>+ Enter a new address</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <AddressPicker
        value={address}
        onChange={handleChange}
        errors={addressErrors}
        errorClassName="border-amber-400 bg-amber-50/50"
        hideErrorMessages
        stateFormat="abbreviation"
        portalContainer={dialogRef?.current}
      />
    </div>
  );
};
